import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { type AdminUserDetail, type UserSearchResult } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableId, CopyableEmail } from "../components/CopyableId";
import { RoleTag, ActiveTag } from "../components/Tags";
import { UserDetailPanel } from "../components/UserDetailPanel";

export function SupportUsersPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "users", label: "Users", active: true, onSelect: () => navigate("/support/users") },
    { id: "guests", label: "Guest users", active: false, onSelect: () => navigate("/support/guest-users") },
    { id: "broken", label: "Broken users", active: false, onSelect: () => navigate("/support/broken-users") },
  ]);

  // All view state lives in the URL so browser back/forward works and rows are deep-linkable:
  //   ?q=<email>   email substring search (identity server)
  //   ?org=<guid>  org-filter mode — list that organization's members (Drawboard Projects)
  //   ?user=<id>   open the detail panel for this id (used by the broken-user report deep links)
  //   ?email=<e>   optional, carries the email for a deep-linked user's panel header
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const orgFilter = params.get("org");
  const selectedUserId = params.get("user");

  const [input, setInput] = useState(q);
  const [results, setResults] = useState<UserSearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Identity's displayName is blank for SAML/external users — the real names live on the Drawboard
  // Projects profile, fetched here as a batch.
  const [names, setNames] = useState<Record<string, string | null>>({});
  // Cross-system mismatch flags (the user is broken either way):
  const [noProfile, setNoProfile] = useState<Set<string>>(new Set()); // identity exists, no Projects profile
  const [noIdentity, setNoIdentity] = useState<Set<string>>(new Set()); // Projects profile exists, no identity
  // Org-role + active flag per member, keyed by userId — populated only in org-filter mode (the org
  // members carry it; a plain email search does not), and drives the Role/Status columns below.
  const [memberMeta, setMemberMeta] = useState<Record<string, { role: string | null; isActive: boolean }>>({});
  // Org-filter mode only: the organization's name, so the banner reads as more than a GUID. Best
  // effort — the member list is the point, so a failed lookup just leaves the id showing.
  const [orgName, setOrgName] = useState<string | null>(null);
  // Client-side narrowing of a long member list (email or name substring).
  const [memberFilter, setMemberFilter] = useState("");

  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => { setMemberFilter(""); }, [orgFilter]);

  // Load results: org-filter mode lists that org's members; otherwise email search on q (>= 2 chars).
  useEffect(() => {
    let cancelled = false;
    setResults(null);
    setError(null);
    setNames({});
    setNoProfile(new Set());
    setNoIdentity(new Set());
    setMemberMeta({});
    setOrgName(null);

    if (orgFilter) {
      setLoading(true);
      api.getOrganization(orgFilter).then(
        (org) => { if (!cancelled) setOrgName(org.name); },
        () => {},
      );
      api.getOrgMembers(orgFilter).then(
        (members) => {
          if (cancelled) return;
          setResults(members);
          setNames(Object.fromEntries(members.map((m) => [m.userId, m.displayName])));
          setMemberMeta(Object.fromEntries(members.map((m) => [m.userId, { role: m.role, isActive: m.isActive }])));
          if (members.length > 0) {
            // Members come from Drawboard Projects — flag any with no identity record (can't sign in).
            api.identityUsersExist(members.map((m) => m.userId)).then(
              (existing) => {
                if (cancelled) return;
                const ex = new Set(existing);
                setNoIdentity(new Set(members.filter((m) => !ex.has(m.userId)).map((m) => m.userId)));
              },
              () => {},
            );
          }
        },
        (e) => { if (!cancelled) setError(`${e}`); },
      ).finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }

    if (q.length >= 2) {
      setLoading(true);
      api.searchUsers(q).then(
        (list) => {
          if (cancelled) return;
          setResults(list);
          // ?open=1 — deep links from the guest-users / invitations email cross-links: when the
          // search pins down exactly one user, open their panel without an extra click.
          if (params.get("open") && list.length === 1) {
            const only = list[0];
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("open");
              next.set("user", only.userId);
              if (only.email) next.set("email", only.email);
              return next;
            }, { replace: true });
          }
          if (list.length > 0) {
            api.getUserNames(list.map((u) => u.userId)).then(
              (rows) => {
                if (cancelled) return;
                setNames(Object.fromEntries(rows.map((r) => [r.userId, r.displayName])));
                setNoProfile(new Set(rows.filter((r) => !r.hasProfile).map((r) => r.userId)));
              },
              () => {},
            );
          }
        },
        (e) => { if (!cancelled) setError(`${e}`); },
      ).finally(() => { if (!cancelled) setLoading(false); });
    }
    return () => { cancelled = true; };
  }, [api, q, orgFilter]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = input.trim();
    if (v.length < 2) {
      setError("Type at least 2 characters of the email to search.");
      return;
    }
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("q", v);
      next.delete("org");
      next.delete("user");
      next.delete("email");
      return next;
    });
  }

  function toggleUser(u: UserSearchResult) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get("user") === u.userId) {
        next.delete("user");
        next.delete("email");
      } else {
        next.set("user", u.userId);
        if (u.email) next.set("email", u.email);
        else next.delete("email");
      }
      return next;
    });
    queueMicrotask(() => {
      document.getElementById("user-detail-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function closePanel() {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("user");
      next.delete("email");
      return next;
    });
  }

  function clearOrgFilter() {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("org");
      next.delete("user");
      next.delete("email");
      return next;
    });
  }

  // Label a row sorts and filters by: the Projects display name where we have one, else the email.
  const labelOf = useCallback(
    (u: UserSearchResult) => (names[u.userId] ?? u.displayName ?? u.email ?? u.userId).toLowerCase(),
    [names],
  );

  // Search results keep the server's order. Member lists get narrowed by the filter box and sorted
  // administrators-first — in a support context the admins are who you're usually looking for, and
  // an org's own list has no meaningful natural order.
  const rows = useMemo(() => {
    if (!results || !orgFilter) return results;
    const needle = memberFilter.trim().toLowerCase();
    const matching = needle
      ? results.filter((u) => (u.email ?? "").toLowerCase().includes(needle) || labelOf(u).includes(needle))
      : results;
    return [...matching].sort((a, b) => {
      const rank = (u: UserSearchResult) => (memberMeta[u.userId]?.role === "Administrator" ? 0 : 1);
      return rank(a) - rank(b) || labelOf(a).localeCompare(labelOf(b));
    });
  }, [results, orgFilter, memberFilter, memberMeta, labelOf]);

  // Banner counts, from the full member list rather than the filtered view.
  const memberStats = useMemo(() => {
    if (!results || !orgFilter) return null;
    let admins = 0;
    let disabled = 0;
    for (const u of results) {
      const meta = memberMeta[u.userId];
      if (meta?.role === "Administrator") admins++;
      if (meta && !meta.isActive) disabled++;
    }
    return { total: results.length, admins, disabled };
  }, [results, orgFilter, memberMeta]);

  // Any panel action that changes the Projects side hands back the fresh detail — fold it into the
  // list so the row doesn't sit there showing the pre-action role, status or email.
  const handleUserChanged = useCallback((detail: AdminUserDetail) => {
    setNames((prev) => ({ ...prev, [detail.userId]: detail.displayName }));
    setMemberMeta((prev) =>
      prev[detail.userId]
        ? { ...prev, [detail.userId]: { role: detail.role, isActive: detail.isActive } }
        : prev,
    );
    setResults((prev) => {
      if (!prev) return prev;
      // Removed from the org (or deleted) — they are no longer a member of the org being listed.
      const kept = orgFilter && !detail.hasOrganization
        ? prev.filter((u) => u.userId !== detail.userId)
        : prev;
      return kept.map((u) =>
        u.userId === detail.userId
          ? { ...u, email: detail.email ?? u.email, displayName: detail.displayName }
          : u,
      );
    });
  }, [orgFilter]);

  // Reflect the selected row in the panel. For a deep link (?user with no matching row loaded yet)
  // synthesize a minimal record — the panel loads both sides by id regardless.
  const selectedUser: UserSearchResult | null = selectedUserId
    ? results?.find((u) => u.userId === selectedUserId) ?? {
        userId: selectedUserId,
        email: params.get("email"),
        displayName: null,
      }
    : null;

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Users</div>
        <h1>Users</h1>
        <p>
          Look up an identity-server user by email, then click a row to open their support panel —
          Drawboard Projects organization/role/active status alongside identity verification, lockout,
          providers, and the password reset.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> {orgFilter ? "Organization members" : "Find user"}
          <span className="hint">
            {orgFilter ? "members of the selected organization" : "case-insensitive substring match on email"}
          </span>
        </header>
        <div className="body">
          {orgFilter ? (
            <>
              <div className="callout">
                <span className="mk">i</span>
                <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11 }}>
                    Members of{" "}
                    <Link
                      to={`/support/organizations?org=${encodeURIComponent(orgFilter)}`}
                      style={{ color: "var(--cyan)" }}
                      title="Open this organization"
                    >
                      {orgName ?? "this organization"}
                    </Link>
                    {memberStats && (
                      <>
                        {" · "}
                        {memberStats.total} member{memberStats.total === 1 ? "" : "s"}
                        {memberStats.admins > 0 && <> · {memberStats.admins} admin{memberStats.admins === 1 ? "" : "s"}</>}
                        {memberStats.disabled > 0 && (
                          <span style={{ color: "var(--red)" }}> · {memberStats.disabled} disabled</span>
                        )}
                      </>
                    )}
                  </span>
                  <CopyableId value={orgFilter} />
                  <span style={{ flex: 1 }} />
                  <button className="btn btn-ghost btn-sm" onClick={clearOrgFilter}>Clear filter</button>
                </div>
              </div>

              {/* Only worth the space once scanning the list by eye stops working. */}
              {results && results.length > 8 && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                  <input
                    type="search"
                    placeholder="Filter members by name or email…"
                    spellCheck={false}
                    autoComplete="off"
                    value={memberFilter}
                    onChange={(e) => setMemberFilter(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {memberFilter.trim() && rows && (
                    <span className="hint" style={{ whiteSpace: "nowrap" }}>
                      {rows.length} of {results.length}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <form className="inline-form" onSubmit={submit}>
                <input
                  type="search"
                  placeholder="alice@example.com"
                  spellCheck={false}
                  autoComplete="off"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
                <button className={"btn btn-primary" + (loading ? " loading" : "")} disabled={loading || input.trim().length < 2}>
                  <span className="glyph" /> Search
                </button>
              </form>
              <div className="hint" style={{ marginTop: 8 }}>
                Substring match on email address — first 20 matches returned.
              </div>
            </>
          )}

          {error && (
            <div className="callout error" style={{ marginTop: 16 }}>
              <span className="mk">err</span>
              <span>{error}</span>
            </div>
          )}

          {rows && (
            <div style={{ marginTop: 18, border: "1px solid var(--line)" }}>
              <table className="grid">
                {/* Width hints — without them the name column collapses to a couple of characters and
                    wraps every name onto two lines, while the id column takes the slack. */}
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "18%" }} />
                  {orgFilter && <col style={{ width: "9%" }} />}
                  {orgFilter && <col style={{ width: "9%" }} />}
                  <col />
                  <col style={{ width: "1%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display name</th>
                    {orgFilter && <th>Role</th>}
                    {orgFilter && <th>Status</th>}
                    <th>User id</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td className="empty" colSpan={orgFilter ? 6 : 4}>
                        {!orgFilter
                          ? <>no users matched <span className="mono">{q}</span></>
                          : memberFilter.trim()
                            ? <>no members matched <span className="mono">{memberFilter.trim()}</span></>
                            : "this organization has no members"}
                      </td>
                    </tr>
                  )}
                  {rows.map((u) => {
                    const isActive = selectedUserId === u.userId;
                    const name = names[u.userId] ?? u.displayName;
                    const meta = memberMeta[u.userId];
                    const brokenTag = noIdentity.has(u.userId)
                      ? "no identity"
                      : noProfile.has(u.userId)
                        ? "no profile"
                        : null;
                    return (
                      <tr
                        key={u.userId}
                        className={isActive ? "row-selected" : undefined}
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleUser(u)}
                      >
                        <td>
                          <CopyableEmail email={u.email} />
                          {brokenTag && (
                            <span
                              className="tag"
                              style={{ marginLeft: 8, color: "var(--red)", borderColor: "var(--red)", fontSize: 10 }}
                              title="Drawboard Projects ↔ identity mismatch — this user is broken"
                            >
                              ⚠ {brokenTag}
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={name ?? undefined}>
                          {name ?? <span className="faint">—</span>}
                        </td>
                        {orgFilter && <td><RoleTag role={meta?.role ?? null} /></td>}
                        {orgFilter && (
                          <td>{meta ? <ActiveTag isActive={meta.isActive} /> : <span className="faint">—</span>}</td>
                        )}
                        <td><CopyableId value={u.userId} /></td>
                        <td className="actions">
                          <button
                            className={"btn btn-sm " + (isActive ? "btn-ghost" : "btn-primary")}
                            onClick={(e) => { e.stopPropagation(); toggleUser(u); }}
                          >
                            {isActive ? "Close" : "Details"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!orgFilter && rows.length === 20 && (
                <div className="hint" style={{ padding: "8px 14px" }}>
                  showing first 20 matches — narrow the search if the user isn't here
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {selectedUser && (
        <UserDetailPanel user={selectedUser} onClose={closePanel} onUserChanged={handleUserChanged} />
      )}
    </>
  );
}
